import dynamic from 'next/dynamic'
import { mdParser, getRandomID, hideAllTooltips } from '@/utils/index'
import api from '@/utils/api'
import { Edit, FileText, Eye, Inbox, Box, Info } from 'react-feather'
import { useState, useMemo, useEffect, useCallback } from 'react'
import {
  updateItem,
  videoProgressState,
  currentAnnotationState,
  isPlayingState,
  createItem,
  isPremiumState,
  transcriptState,
  useIsRowView,
  isTrialState,
} from '@/store'
import { useRecoilState } from 'recoil'
import { useSettings } from '@/pages/settings'
import analytics from '@/utils/analytics'
import useSetAnnotationFromProgress from '@/components/AnnotationsView/useSetAnnotationFromProgress'
import Tab from '@/components/AnnotationsView/tabs/Tab'
import toast from 'react-hot-toast'
import EditorTab from '@/components/AnnotationsView/tabs/EditorTab'
import TranscriptTab from '@/components/AnnotationsView/tabs/TranscriptTab'
import ChatWithTranscript from '@/components/AnnotationsView/tabs/ChatWithTranscript'
import { useSession } from 'next-auth/client'
import { getIsYearlyBilling, getIsMonthlyBilling } from '@/pages/settings'
import { ChatWithTranscriptTab } from './tabs/ChatWithTranscriptTab'

const MdEditor = dynamic(() => import('react-markdown-editor-lite'), {
  ssr: false,
})

export default function Editor({
  video,
  setAnnotations,
  isReadOnly,
  isDemo = false,
  annotations,
  demoAnnotation,
  isDragging,
  isHovering,
  shouldCreate = () => true,
  setShowDemo = () => {},
}) {
  const [currentView, setCurrentView] = useState('edit')
  const [isLoading, setIsLoading] = useState(false)
  const [newAnnotationStart, setNewAnnotationStart] = useState()
  const [progress] = useRecoilState(videoProgressState)
  const [isPlaying, setIsPlaying] = useRecoilState(isPlayingState)
  const [settings] = useSettings()
  const [isPremium] = useRecoilState(isPremiumState)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const isRowView = useIsRowView()
  const [, setTranscript] = useRecoilState(transcriptState)
  const [currentAnnotation, setCurrentAnnotation] = useRecoilState(
    currentAnnotationState
  )
  const [session] = useSession()
  const [isTrial] = useRecoilState(isTrialState)

  const setCurrentViewAndTrack = useCallback((view) => {
    setCurrentView(view)
    analytics.track(`Clicked on ${view} tab`)
  }, [])

  const isBetaUser = [
    'matiastucci@gmail.com',
    'mati@tucci.me',
    'nicolas.delvalle@gmail.com',
    // 'mati@annotate.tv',
  ].includes(session?.user?.email)

  const isYoutubeVideo = video.platform === 'youtube'

  const showChatTabOption = useMemo(() => {
    if (!isYoutubeVideo) {
      return false
    }

    if (isBetaUser || !isPremium || isTrial) {
      return true
    }

    // NOT SHOWING TAB TO LIFETIME USERS FOR NOW
    if (isPremium) {
      return (
        getIsMonthlyBilling(settings?.billing) ||
        getIsYearlyBilling(settings?.billing)
      )
    }

    return false
  }, [isYoutubeVideo, isBetaUser, isPremium, settings?.billing, isTrial])

  const videoID = video._id
  const showDragging = isHovering || isDragging
  const description = video.description

  useSetAnnotationFromProgress({
    setCurrentAnnotation,
    progress,
    isReadOnly,
    annotations,
  })

  useEffect(() => {
    return () => {
      setTranscript(null)
    }
  }, [])

  useEffect(() => {
    if (!isDemo) {
      return
    }

    if (demoAnnotation) {
      onTitleChange(demoAnnotation.title)
      onContentChange({ text: demoAnnotation.content })
    } else {
      resetState()
    }
  }, [demoAnnotation, isDemo])

  useEffect(() => {
    setTitle((currentAnnotation && currentAnnotation.title) || '')
    setContent((currentAnnotation && currentAnnotation.content) || '')
    if (currentAnnotation && currentAnnotation.start) {
      setNewAnnotationStart(currentAnnotation.start)
    } else {
      setNewAnnotationStart(null)
    }
  }, [currentAnnotation])

  function onTitleChange(text) {
    if (!newAnnotationStart) {
      const start = progress ? progress.playedSeconds : 0
      setNewAnnotationStart(start)
    }

    if (isPlaying && settings && settings.pauseOnType) {
      setIsPlaying(false)
    }

    const title = text

    if (!title && !content) {
      resetState()
    }

    setTitle(title)
  }

  function onContentChange({ text }) {
    if (!newAnnotationStart) {
      const start = progress ? progress.playedSeconds : 0
      setNewAnnotationStart(start)
    }

    if (isPlaying && settings && settings.pauseOnType) {
      setIsPlaying(false)
    }

    if (!text && !title) {
      resetState()
    }

    setContent(text)
  }

  function setNewContent({ text, start, pauseOnType = true }) {
    if (!newAnnotationStart) {
      setNewAnnotationStart(start)
    }

    if (isPlaying && pauseOnType && settings && settings.pauseOnType) {
      setIsPlaying(false)
    }

    setContent(text)
  }

  // TODO: try useCallback
  async function onCreate(shouldReset = true, _content, _start) {
    hideAllTooltips()

    const newStart = _start || newAnnotationStart
    const newContent = _content || content

    const annotationWithSameStartTime = annotations?.find(
      ({ start }) => start === newStart
    )

    if (annotationWithSameStartTime) {
      toast.error('You already have an annotation with this start time')
      return
    }

    if (!title && !newContent) {
      toast.error('The title or content of the annotation is required')
      return
    }

    if (!shouldCreate()) {
      return
    }

    try {
      analytics.track(`${isDemo ? '(Demo) ' : ''}Creating annotation`)
      setIsLoading(true)

      let response

      if (isDemo) {
        response = {
          data: {
            data: {
              _id: getRandomID(),
              title,
              content: newContent,
              video: videoID,
              start: newStart,
            },
          },
        }
      } else {
        response = await api.post('/annotations', {
          title,
          content: newContent,
          video: videoID,
          start: newStart,
        })
      }

      analytics.track(`${isDemo ? '(Demo) ' : ''}Annotation created`)

      const newAnnotation = response.data.data

      if (!isDemo && isPremium) {
        api.post(`/readwise/${newAnnotation._id}`)
      }

      setAnnotations((prev) =>
        createItem({ items: prev, item: newAnnotation }).sort(
          (a, b) => a.start - b.start
        )
      )

      if (!isPlaying) {
        setIsPlaying(true)
      }

      if (shouldReset) {
        resetState()
      }

      return newAnnotation
    } catch (error) {
      analytics.track(`${isDemo ? '(Demo) ' : ''}Error creating annotation`)
      toast.error('Something went wrong')
    } finally {
      setIsLoading(false)
    }
  }

  function onUpdate() {
    hideAllTooltips()

    try {
      analytics.track(`${isDemo ? '(Demo) ' : ''}Updating annotation`)
      setIsLoading(true)

      if (!isDemo) {
        api.put(`/annotations/${currentAnnotation._id}`, {
          title,
          content,
          start: newAnnotationStart,
        })

        if (isPremium) {
          api.post(`/readwise/${currentAnnotation._id}`)
        }
      }

      setAnnotations((prev) =>
        updateItem({
          items: prev,
          item: {
            ...currentAnnotation,
            start: newAnnotationStart,
            title,
            content,
          },
        }).sort((a, b) => a.start - b.start)
      )

      if (!isPlaying) {
        setIsPlaying(true)
      }

      resetState()
    } catch (error) {
      analytics.track(`${isDemo ? '(Demo) ' : ''}Error updating annotation`)
      toast.error('Something went wrong')
    } finally {
      setIsLoading(false)
    }
  }

  function resetState() {
    hideAllTooltips()
    setNewAnnotationStart(null)
    setCurrentAnnotation(null)
    setTitle('')
    setContent('')
  }

  const className = `relative flex flex-1 flex-col px-5 ${
    isRowView ? 'py-5' : 'h-screen pb-5 overflow-auto'
  }`

  if (!currentAnnotation && isReadOnly) {
    return (
      <div className={className}>
        <DraggingCover isDragging={showDragging} />

        <div className="flex-1 overflow-auto px-1">
          <div className="bg-white w-full h-full border-gray-200 border center flex-col">
            <Inbox
              className="mb-3 text-gray-500 stroke-1"
              style={{ width: '50px', height: '50px' }}
            />
            <h1 className="text-gray-500 text-lg">
              Please, select an annotation.
            </h1>
          </div>
        </div>
      </div>
    )
  }

  if (isReadOnly) {
    return (
      <div className={className}>
        <DraggingCover isDragging={showDragging} />

        <div className="flex-1 overflow-auto px-1">
          <NoteContentPreview title={title} content={content} />
        </div>
      </div>
    )
  }

  return (
    <div className={`${className} pb-5 h-0 editor-content`}>
      <DraggingCover isDragging={showDragging} />

      <nav
        className={`-mb-px flex space-x-8 pb-3 overflow-x-auto ${
          isRowView ? 'pt-2' : ''
        }`}
        aria-label="Tabs"
      >
        <Tab
          Icon={Edit}
          keyName="edit"
          activeTab={currentView}
          setActiveTab={setCurrentViewAndTrack}
          name="Editor"
        />
        {showChatTabOption && (
          <Tab
            Icon={Box}
            keyName="chat"
            activeTab={currentView}
            setActiveTab={setCurrentViewAndTrack}
            name={<ChatWithTranscriptTab />}
          />
        )}
        {isYoutubeVideo && (
          <Tab
            Icon={FileText}
            keyName="transcript"
            activeTab={currentView}
            setActiveTab={setCurrentViewAndTrack}
            name="Transcript"
          />
        )}
        <Tab
          Icon={Eye}
          keyName="preview"
          activeTab={currentView}
          setActiveTab={setCurrentViewAndTrack}
          name="Preview"
        />
        <Tab
          Icon={Info}
          keyName="description"
          activeTab={currentView}
          setActiveTab={setCurrentViewAndTrack}
          name="Description"
        />
      </nav>

      <div className="flex flex-1 flex-col overflow-auto px-1">
        {currentView === 'edit' && (
          <EditorTab
            {...{
              video,
              isDemo,
              setCurrentView,
              title,
              content,
              onTitleChange,
              onContentChange,
              setNewContent,
              resetState,
              onUpdate,
              onCreate,
              newAnnotationStart,
              setNewAnnotationStart,
              isLoading,
            }}
          />
        )}

        {currentView === 'preview' && (
          <NoteContentPreview title={title} content={content} />
        )}

        {currentView === 'transcript' && (
          <TranscriptTab
            videoUrl={video.url}
            videoTitle={video.title}
            duration={video.duration}
            currentView={currentView}
            setCurrentView={setCurrentView}
            setTitle={setTitle}
            content={content}
            setNewContent={setNewContent}
            onCreate={onCreate}
            isDemo={isDemo}
          />
        )}

        {currentView === 'description' && (
          <Description description={description} />
        )}

        <ChatWithTranscript
          video={video}
          user={session?.user}
          isVisible={currentView === 'chat'}
          setShowDemo={setShowDemo}
          isDemo={isDemo}
        />
      </div>
    </div>
  )
}

function NoteContentPreview({ title, content }) {
  return (
    <div className="flex flex-col h-full">
      {title && (
        <div className="mb-3">
          <h2 className="font-semibold text-lg">{title}</h2>
        </div>
      )}

      <MdEditor
        style={{ flex: '1', height: '100%' }}
        value={content}
        config={{
          view: {
            menu: false,
            md: false,
            html: true,
          },
          canView: {
            menu: false,
            md: false,
            html: true,
            fullScreen: false,
            hideMenu: false,
          },
        }}
        renderHTML={(text) => mdParser.render(text)}
      />
    </div>
  )
}

function Description({ description }) {
  return (
    <div
      className="flex flex-col h-full relative overflow-x-auto overflow-y-hidden bg-white p-3"
      style={{ border: '1px solid rgb(224, 224, 224)' }}
    >
      <div
        className="whitespace-pre-line"
        style={{ flex: '1', height: '100%' }}
      >
        {description || 'No description'}
      </div>
    </div>
  )
}

function DraggingCover({ isDragging }) {
  if (!isDragging) {
    return null
  }

  return (
    <div className="absolute inset-0 z-10" style={{ cursor: 'row-resize' }} />
  )
}
